import React, { FC, useCallback, useState } from 'react';
import styled from 'styled-components';
import _isArray from 'lodash/isArray';
import _isBoolean from 'lodash/isBoolean';
import _isString from 'lodash/isString';
import _take from 'lodash/take';
import UncontrolledTooltip from '@minecraft.uncontrolled-tooltip';
import { useTranslation } from '@blocs.i18n';
import { PillType } from '../../../shared/utils';
import { Pill } from '../../../shared/components';
import FilterBasicBreadboxSeeMoreValuesModal from '../FilterBasicBreadboxSeeMoreValuesModal';

const VISIBLE_TOOLTIP_ITEMS_LENGTH_BY_DEFAULT = 5;

const StyledSeeMoreButton = styled.div`
  text-decoration: underline;
`;

const StyledPillLabel = styled.div`
  white-space: pre-wrap;
`;

type Props = {
  className?: string;
  handleClear: () => void;
  isNotAppliedFilterPills: boolean;
  pill: PillType;
  pillKey: string;
  showClearIcon?: boolean;
};

interface PillTooltipProps extends Pick<Props, 'pill' | 'pillKey'> {
  toggleSeeMoreValuesModal: () => void;
}

const PillTooltipContent: FC<Omit<PillTooltipProps, 'pillKey'>> = ({ pill, toggleSeeMoreValuesModal }) => {
  const { t } = useTranslation();

  if (_isArray(pill.value) && pill.value.length > 0) {
    const visibleItems: any[] = _take(pill.value, VISIBLE_TOOLTIP_ITEMS_LENGTH_BY_DEFAULT);
    const hiddenItemsCount = pill.value.length - visibleItems.length;

    return (
      <>
        {visibleItems.map((item) => {
          if (!item) {
            return null;
          }

          const labelKeyVal = item?.key && t(item?.key);
          const labelVal = item?.label;

          return <StyledPillLabel key={item?.value || item}>{labelKeyVal || labelVal}</StyledPillLabel>;
        })}
        {hiddenItemsCount > 0 && (
          <StyledSeeMoreButton role="button" onClick={toggleSeeMoreValuesModal}>
            {t('common:button.seeMore')}
          </StyledSeeMoreButton>
        )}
      </>
    );
  }

  if (_isString(pill.value)) {
    return <StyledPillLabel>{pill.value}</StyledPillLabel>;
  }

  return null;
};

const PillTooltip: FC<PillTooltipProps> = ({ pill, pillKey, toggleSeeMoreValuesModal }) => {
  return (
    !_isBoolean(pill.value) && (
      <UncontrolledTooltip autohide={false} placement="top" target={pillKey} data-testid={`breadbox-pill-${pillKey}`}>
        <PillTooltipContent pill={pill} toggleSeeMoreValuesModal={toggleSeeMoreValuesModal} />
      </UncontrolledTooltip>
    )
  );
};

const getPillText = (pill: PillType): string => {
  if (_isArray(pill.value) && pill.value.length > 1) {
    return `${pill.name} (${pill.value.length})`;
  }

  return typeof pill?.name === 'string' ? pill?.name : pill?.name?.label;
};

const FilterBasicBreadboxPill: FC<Props> = ({
  className,
  handleClear,
  isNotAppliedFilterPills,
  pill,
  pillKey,
  showClearIcon = true,
}) => {
  const [isSeeMoreValuesModalOpened, setSeeMoreValuesModalOpened] = useState(false);
  const toggleSeeMoreValuesModal = useCallback(() => {
    setSeeMoreValuesModalOpened(!isSeeMoreValuesModalOpened);
  }, [isSeeMoreValuesModalOpened]);

  return (
    <>
      <Pill
        isNotAppliedFilterPills={isNotAppliedFilterPills}
        pillKey={pillKey}
        text={getPillText(pill)}
        onClick={handleClear}
        className={className}
        showClearIcon={showClearIcon}
      />
      <PillTooltip pill={pill} toggleSeeMoreValuesModal={toggleSeeMoreValuesModal} pillKey={pillKey} />
      {isSeeMoreValuesModalOpened && (
        <FilterBasicBreadboxSeeMoreValuesModal
          pillKey={pillKey}
          pillValues={pill.value}
          toggleModal={toggleSeeMoreValuesModal}
        />
      )}
    </>
  );
};

export default FilterBasicBreadboxPill;
